// frontend/src/pages/SingleClassManagementPage.js
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Spin, Typography, Button, Space, Tooltip, Card, Row, Col, Statistic, App as AntApp, InputNumber, Select } from 'antd';
import { ArrowLeftOutlined, EyeOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { getClassSummary } from '../services/classService';
import useResponsive from '../hooks/useResponsive';
import { useAcademicYear } from '../context/AcademicYearContext';

const { Title, Text } = Typography;

const SingleClassManagementPage = () => {
    const { id: classId } = useParams();
    const navigate = useNavigate();
    const goBack = () => navigate(-1);
    const { message } = AntApp.useApp();
    const { isMobile } = useResponsive();
    const { currentYear } = useAcademicYear();

    const [classData, setClassData] = useState(null);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [subjects, setSubjects] = useState([]);
    const [exams, setExams] = useState([]);
    const [selectedExam, setSelectedExam] = useState(null);
    const [pageSize, setPageSize] = useState(isMobile ? 10 : 25);

    const getColumnPercentageFilterProps = (dataIndex, isNested = false) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
            const range = selectedKeys[0] || { from: null, to: null };
            const setRange = (newRange) => setSelectedKeys(newRange.from !== null || newRange.to !== null ? [newRange] : []);

            return (
                <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Space>
                        <InputNumber
                            placeholder="From %"
                            min={0}
                            max={100}
                            value={range.from}
                            onChange={(value) => setRange({ ...range, from: value })}
                            style={{ width: 100 }}
                        />
                        <InputNumber
                            placeholder="To %"
                            min={0}
                            max={100}
                            value={range.to}
                            onChange={(value) => setRange({ ...range, to: value })}
                            style={{ width: 100 }}
                        />
                    </Space>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                        <Button
                            onClick={() => {
                                clearFilters();
                                confirm({ closeDropdown: true });
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => confirm({ closeDropdown: true })}
                            size="small"
                            style={{ width: 90 }}
                        >
                            OK
                        </Button>
                    </div>
                </div>
            );
        },
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) => {
            const { from, to } = value;
            let percentage;
            if (isNested) {
                percentage = record[dataIndex]?.percentage;
            } else {
                percentage = record[dataIndex];
            }

            if (percentage === undefined || percentage === null || percentage === '-') {
                return false;
            }

            const isFromOk = from === null || from === undefined || percentage >= from;
            const isToOk = to === null || to === undefined || percentage <= to;

            return isFromOk && isToOk;
        },
    });

    useEffect(() => {
        if (classId && currentYear) {
            fetchClassSummary();
        }
    }, [classId, currentYear]);

    const fetchClassSummary = async () => {
        setLoading(true);
        try {
            const data = await getClassSummary(classId);
            // console.log('Raw class summary data:', data);
            // console.log('Subjects received:', data.subjects);
            // console.log('Number of subjects:', data.subjects?.length || 0);
            setClassData(data.class || {});
            setStudents(data.students || []);
            setSubjects(data.subjects || []);
            setExams(data.exams || []);
            // Set first exam as default if available
            if (data.exams && data.exams.length > 0) {
                setSelectedExam(data.exams[0].id);
            }
            // console.log('State updated - Subjects:', data.subjects);
        } catch (error) {
            console.error('Class summary error:', error);
            message.error('Failed to load class summary: ' + (error.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        goBack();
    };

    const handleViewStudent = (studentId) => {
        navigate(`/students/${studentId}`);
    };

    const handleEditStudent = (studentId) => {
        navigate(`/students/edit/${studentId}`);
    };

    // Prepare table data with calculated attendance, marks, and exam scores
    // console.log('Building table data - subjects count:', subjects.length);
    // console.log('Subjects array:', subjects);
    // console.log('Selected exam:', selectedExam);
    const tableData = students.map((student) => {
        const attendancePercentage = student.attendance_percentage || 0;
        const totalDays = student.total_attendance_days || 0;
        const presentDays = student.present_days || 0;
        const attendanceText = totalDays > 0 ? `${presentDays}/${totalDays}` : '0/0';

        // Build exam scores data for selected exam only
        const selectedExamScores = {};
        if (selectedExam && student.examScores && student.examScores[selectedExam]) {
            const examData = student.examScores[selectedExam];
            if (examData && examData.subjects) {
                subjects.forEach(subject => {
                    const scoreData = examData.subjects[subject.id];
                    if (scoreData) {
                        const key = `exam_${selectedExam}_subject_${subject.id}`;
                        const score = scoreData.score;
                        const total_marks = scoreData.total_marks;
                        const percentage = total_marks > 0 ? ((score / total_marks) * 100) : 0;
                        selectedExamScores[key] = {
                            score: score,
                            total_marks: total_marks,
                            exam_name: examData.exam_name,
                            subject_name: scoreData.subject_name,
                            percentage: percentage.toFixed(2)
                        };
                    }
                });
            }
        }

        return {
            key: student.id,
            rollNo: student.roll_no || '-',
            name: student.student_name || 'N/A',
            attendance: {
                text: attendanceText,
                percentage: attendancePercentage,
            },
            ...selectedExamScores,
            studentId: student.id,
        };
    });

    // Build columns dynamically
    // console.log('Creating columns - Subjects:', subjects);
    // console.log('Creating columns - Subject count:', subjects.length);

    // Find selected exam name
    const selectedExamName = exams.find(e => e.id === selectedExam)?.name || 'Select Exam';

    const columns = [
        {
            title: 'Roll No',
            dataIndex: 'rollNo',
            key: 'rollNo',
            width: 80,
            fixed: 'left',
            sorter: (a, b) => (parseInt(a.rollNo, 10) || 0) - (parseInt(b.rollNo, 10) || 0),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: isMobile ? 150 : 200,
            fixed: 'left',
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: 'Attendance',
            dataIndex: 'attendance',
            key: 'attendance',
            width: 120,
            render: (attendance) => (
                <Tooltip title={`${attendance.percentage.toFixed(2)}%`}>
                    <Text>{attendance.text}</Text>
                </Tooltip>
            ),
            sorter: (a, b) => a.attendance.percentage - b.attendance.percentage,
            ...getColumnPercentageFilterProps('attendance', true),
        },
        // Add columns only for selected exam
        ...(selectedExam ? subjects.map((subject) => ({
            title: (
                <div style={{ textAlign: 'center' }}>
                    <div>{subject.name}</div>
                    <div style={{ fontSize: '0.75em', color: '#666', marginTop: '4px' }}>
                        {subject.teacher_full_name || subject.teacher_name || 'Unassigned'}
                    </div>
                </div>
            ),
            dataIndex: `exam_${selectedExam}_subject_${subject.id}`,
            key: `exam_${selectedExam}_subject_${subject.id}`,
            width: 120,
            align: 'center',
            render: (scoreData) => {
                if (!scoreData || typeof scoreData !== 'object') {
                    return <Text>-</Text>;
                }
                const score = scoreData.score == null ? scoreData.score : Number(scoreData.score);
                const totalMarks = scoreData.total_marks == null ? scoreData.total_marks : Number(scoreData.total_marks);
                if (score === null || score === undefined || totalMarks === null || totalMarks === undefined) {
                    return <Text>-</Text>;
                }
                const percentage = totalMarks > 0 ? ((score / totalMarks) * 100).toFixed(2) : 0;
                const displayPercentage = Number(percentage).toString();
                return (
                    <Tooltip title={`Score: ${score}/${totalMarks} (${displayPercentage}%)`}>
                        <Text strong>{score}/{totalMarks} ({displayPercentage}%)</Text>
                    </Tooltip>
                );
            },
            sorter: (a, b) => {
                const scoreA = a[`exam_${selectedExam}_subject_${subject.id}`]?.score || 0;
                const scoreB = b[`exam_${selectedExam}_subject_${subject.id}`]?.score || 0;
                return scoreA - scoreB;
            },
            ...getColumnPercentageFilterProps(`exam_${selectedExam}_subject_${subject.id}`, true),
        })) : []),
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="View">
                        <Button
                            type="text"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => handleViewStudent(record.studentId)}
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditStudent(record.studentId)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: isMobile ? '16px' : '24px' }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={handleBack}
                    style={{ marginBottom: '16px' }}
                >
                    Back
                </Button>
                <Title level={2} style={{ margin: 0 }}>
                    {classData.name} - Class Management {classData.teacher_name && `(Teacher: ${classData.teacher_name})`}
                </Title>
            </div>

            {/* Summary Statistics */}
            <Card style={{ marginBottom: '24px' }}>
                <Row gutter={16}>
                    <Col xs={24} sm={12} lg={6}>
                        <Statistic
                            title="Total Students"
                            value={students.length}
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Statistic
                            title="Total Subjects"
                            value={subjects.length}
                        />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                        <Statistic
                            title="Avg Attendance"
                            value={
                                students.length > 0
                                    ? (
                                        students.reduce((sum, s) => sum + (s.attendance_percentage || 0), 0) /
                                        students.length
                                    ).toFixed(2)
                                    : 0
                            }
                            suffix="%"
                        />
                    </Col>
                </Row>
            </Card>

            {/* Students Table */}
            <Card>
                {/* Exam Term Selector */}
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <Text strong>Select Exam/Term:</Text>
                    <Select
                        placeholder="Select an exam term"
                        style={{ width: 200 }}
                        value={selectedExam}
                        onChange={setSelectedExam}
                        options={exams.map(exam => ({
                            label: exam.name,
                            value: exam.id
                        }))}
                    />
                </div>

                <Table
                    dataSource={tableData}
                    columns={columns}
                    pagination={{
                        pageSize: pageSize,
                        showSizeChanger: true,
                        pageSizeOptions: [10, 25, 50, 100],
                        onShowSizeChange: (current, size) => setPageSize(size),
                        onChange: (page, size) => setPageSize(size),
                        showTotal: (total) => `Total ${total} students`,
                    }}
                    scroll={{ x: 'max-content' }}
                    loading={loading}
                    size={isMobile ? 'small' : 'middle'}
                />
            </Card>
        </div>
    );
};

export default SingleClassManagementPage;
